import React, { useState, useEffect } from 'react';
import '../styles/analytics-responsive.css';
import { cleanCategoryName, formatYAxisNumber } from '../utils';



import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';

// ─── DATA ────────────────────────────────────────────────────────────────────
const defaultHealthData = {
  statusBreakdown: { paid: '0.0', pending: '0.0', overdue: '0.0', failed: '0.0' }
};

// ─── STAT CARD ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, change, changeColor }) => {
  const isPositive = changeColor === '#27ae60';
  return (
    <div className="analytics-stat-card" style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 8,
      padding: '14px 16px',
      flex: 1,
      minWidth: 0,
    }}>
      <div className="stat-label" style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap', marginBottom: 4 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change && <div style={{ fontSize: 10, color: changeColor || '#27ae60', fontWeight: 600 }}>{isPositive ? '▲' : '▼'} {change}</div>}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const getTotal = (data) => data.reduce((sum, d) => sum + d.value, 0);

const RADIAN = Math.PI / 180;

const buildPieLabelLayout = (data, cx, cy, outerRadius) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const labelRadius = outerRadius + 28;
  const bendOffset = 12;
  const minGap = 15;

  let cumulative = 0;
  const items = data.map((d) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 360;
    const midAngle = (startAngle + endAngle) / 2;
    const percent = d.value / total;

    const edgeX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const edgeY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const side = Math.cos(-midAngle * RADIAN) >= 0 ? 'right' : 'left';

    return { name: d.name, color: d.color, percent, edgeX, edgeY, rawY, side };
  });

  ['left', 'right'].forEach((side) => {
    const group = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => item.side === side)
      .sort((a, b) => a.rawY - b.rawY);

    for (let k = 1; k < group.length; k++) {
      if (group[k].rawY - group[k - 1].rawY < minGap) {
        group[k].rawY = group[k - 1].rawY + minGap;
      }
    }
    group.forEach((item) => { items[item.i].finalY = item.rawY; });
  });

  items.forEach((item) => {
    if (item.finalY === undefined) item.finalY = item.rawY;
    item.finalX = item.side === 'right' ? cx + labelRadius : cx - labelRadius;
    item.bendX = item.side === 'right' ? cx + outerRadius + bendOffset : cx - outerRadius - bendOffset;
  });

  return items;
};






const Financeanalytics = () => {
  const [activeFilter, setActiveFilter] = useState('1yr');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [planData, setPlanData] = useState([]);
  const [topCustomersData, setTopCustomersData] = useState([]);
  const [healthData, setHealthData] = useState(defaultHealthData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ['1mo', '3mo', '6mo', '1yr'];

  const renderCustomLabel = (props) => {
    const { cx, cy, outerRadius, index, percent } = props;
    if (percent < 0.005) return null;

    const layout = buildPieLabelLayout(pieChartData, cx, cy, outerRadius);
    const pos = layout[index];
    if (!pos) return null;

    const _n = cleanCategoryName(pos.name); const shortName = _n.length > 16 ? _n.slice(0, 14) + '…' : _n;
    const anchor = pos.side === 'right' ? 'start' : 'end';
    const textX = pos.finalX + (pos.side === 'right' ? 6 : -6);

    return (
      <g>
        <polyline
          points={`${pos.edgeX},${pos.edgeY} ${pos.bendX},${pos.finalY} ${pos.finalX},${pos.finalY}`}
          fill="none"
          stroke="#c7c7c7"
          strokeWidth={1}
        />
        <circle cx={pos.finalX} cy={pos.finalY} r={2} fill={pos.color || '#999'} />
        <text x={textX} y={pos.finalY} textAnchor={anchor} dominantBaseline="central" style={{ fontSize: 9.5 }}>
          <tspan fill="#555">{shortName}</tspan>
          <tspan fill="#1a1a2e" fontWeight={700}>{`  ${(pos.percent * 100).toFixed(0)}%`}</tspan>
        </text>
      </g>
    );
  };

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const API = process.env.REACT_APP_BASE_API_URL || 'https://searchmytenders.in';
        const res = await fetch(`${API}/api/finance-analytics/kpis?filter=${activeFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setKpiData(data.kpiData);
          if (data.chartData) {
            let parsedData = data.chartData;
            if (activeFilter === '1yr' || activeFilter === '6mo') {
              const monthsCount = activeFilter === '1yr' ? 12 : 6;
              const now = new Date();
              const padded = [];
              for (let i = monthsCount - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Revenue: 0, Outstanding: 0 });
              }
              parsedData = padded;
            }
            setChartData(parsedData);
          }
          if (data.pieChartData) setPieChartData(data.pieChartData);
          if (data.planData) setPlanData(data.planData);
          if (data.topCustomersData) setTopCustomersData(data.topCustomersData);
          if (data.healthData) setHealthData(data.healthData);
        } else {
          setError(data.message || 'Failed to load data');
        }
      } catch (err) {
        console.error('Failed to fetch KPI data', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, [activeFilter]);

  return (
    <div className="analytics-root" style={{
      minWidth: 1024,
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '0 4px'
    }}>

      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;

        }
      `}</style>

      {/* ── PAGE TITLE + FILTER ── */}
      <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h4 style={{ fontWeight: 700, fontSize: 20, marginBottom: 2, color: '#1a1a2e' }}>Finance Analytics</h4>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Revenue intelligence and collections tracking</p>
        </div>
        <div className="analytics-filters" style={{ display: 'flex', gap: 8 }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: 50,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── STAT CARDS ROW ── */}
      <div className="analytics-stats-row" style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {error ? (
          <div style={{ padding: '20px', color: '#e74c3c', fontSize: 14, fontWeight: 'bold' }}>Error: {error}</div>
        ) : loading || !kpiData ? (
          <div style={{ padding: '20px', color: '#888', fontSize: 14 }}>Loading KPIs...</div>
        ) : (
          <>
            <StatCard label="Total Revenue" value={kpiData.totalRevenue.value} sub="Collected this period" change={`${Math.abs(kpiData.totalRevenue.change)}% vs last period`} changeColor={kpiData.totalRevenue.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Outstanding Amount" value={kpiData.outstanding.value} sub="Pending & Overdue" change={`${Math.abs(kpiData.outstanding.change)}% vs last period`} changeColor={kpiData.outstanding.change <= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Total Invoices" value={kpiData.totalInvoices.value} sub="Issued this period" change={`${Math.abs(kpiData.totalInvoices.change)}% vs last period`} changeColor={kpiData.totalInvoices.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Paid Invoices" value={kpiData.paidInvoices.value} sub="Cleared invoices" change={`${Math.abs(kpiData.paidInvoices.change)}% vs last period`} changeColor={kpiData.paidInvoices.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Pending Invoices" value={kpiData.pendingInvoices.value} sub="Awaiting payment" change={`${Math.abs(kpiData.pendingInvoices.change)}% vs last period`} changeColor={kpiData.pendingInvoices.change <= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Overdue Invoices" value={kpiData.overdueInvoices.value} sub="Late payments" change={`${Math.abs(kpiData.overdueInvoices.change)}% vs last period`} changeColor={kpiData.overdueInvoices.change <= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Success Rate" value={kpiData.successRate.value} sub="Collection efficiency" change={`${Math.abs(kpiData.successRate.change)}% vs last period`} changeColor={kpiData.successRate.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Total Customers" value={kpiData.customers?.total || 0} sub={`${kpiData.customers?.paid || 0} paid, ${kpiData.customers?.unpaid || 0} unpaid`} />
          </>
        )}
      </div>

      {/* ── ROW 2: AREA CHART + PIE CHART ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        {/* Revenue Over Time */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 58%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
            {activeFilter === '1mo' ? 'Daily Revenue Activity' : activeFilter === '3mo' ? 'Weekly Revenue Activity' : 'Monthly Revenue Activity'}
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Revenue collected vs Outstanding amounts</div>
          <div style={{ flex: 1, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
              ) : (
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#27ae60" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#27ae60" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="colorOut" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e74c3c" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#e74c3c" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ dy: 10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                  <YAxis
                    tick={{ dx: -10, fontSize: 16, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                    width={55}
                    tickFormatter={(value) => {
                      if (value >= 10000000) return `₹${(value / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
                      if (value >= 100000) return `₹${(value / 100000).toFixed(1).replace(/\.0$/, '')}L`;
                      if (value >= 1000) return `₹${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
                      return `₹${value}`;
                    }}
                  />
                  <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }} formatter={(value) => `₹${value.toLocaleString()}`} />
                  <Legend iconSize={18} wrapperStyle={{ fontSize: 14, paddingTop: 8 }} formatter={(value) => <span style={{ color: '#666' }}>{value}</span>} />
                  <Area type="monotone" dataKey="Revenue" stroke="#27ae60" strokeWidth={2} fill="url(#colorRev)" dot={false} />
                  <Area type="monotone" dataKey="Outstanding" stroke="#e74c3c" strokeWidth={2} fill="url(#colorOut)" dot={false} />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Invoice Status Distribution */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 38%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Invoice Status</div>
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 4 }}>Distribution of your generated invoices</div>
          <div style={{ flex: 1, minHeight: 230 }}>
            <ResponsiveContainer width="100%" height="100%">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
              ) : (
                <PieChart margin={{ top: 16, right: 65, left: 65, bottom: 16 }}>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    isAnimationActive={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} Invoices`, name]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
            {[...pieChartData]
              .sort((a, b) => b.value - a.value)
              .map(c => {
                const total = getTotal(pieChartData);
                const pct = total ? ((c.value / total) * 100).toFixed(0) : 0;
                return (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                    <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanCategoryName(c.name)}</span>
                    <span style={{ color: '#333', fontWeight: 600 }}>{pct}%</span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* ── ROW 3: HORIZONTAL BAR + VERTICAL BAR ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        {/* Revenue By Plan */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 45%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Revenue By Plan</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Top earning subscription plans</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
              ) : (
                <BarChart layout="vertical" data={planData} margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ dy: 10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={(val) => `₹${(val / 1000)}k`} />
                  <YAxis tickFormatter={formatYAxisNumber} type="category" dataKey="name" tick={{ dx: -10, fontSize: 16, fill: '#555' }} width={90} axisLine={false} tickLine={false} />
                  <Tooltip cursor={false} formatter={(value, name) => [`₹${value.toLocaleString()}`, name]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#3498db" radius={[0, 3, 3, 0]} barSize={14} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Defaulters / Pending by Customer */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 50%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Top Customers by Outstanding Balance</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Accounts with the highest pending amounts</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
              ) : (
                <BarChart data={topCustomersData} margin={{ top: 0, right: 10, left: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ dy: 10, fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ dx: -10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={(val) => `₹${(val / 1000)}k`} />
                  <Tooltip cursor={false} formatter={(value, name) => [`₹${value.toLocaleString()}`, "Outstanding"]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#e74c3c" radius={[3, 3, 0, 0]} barSize={22} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── ROW 4: PIPELINE HEALTH ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 8 }}>
        {/* Pipeline Health Overview */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 100%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Collections Health Overview</div>

          {/* 4 metric boxes */}
          <div className="analytics-health-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Paid', value: `${healthData.statusBreakdown.paid}%`, color: '#27ae60' },
              { label: 'Pending', value: `${healthData.statusBreakdown.pending}%`, color: '#f39c12' },
              { label: 'Overdue', value: `${healthData.statusBreakdown.overdue}%`, color: '#e74c3c' },
              { label: 'Failed', value: `${healthData.statusBreakdown.failed}%`, color: '#34495e' },
            ].map(m => (
              <div key={m.label} style={{ border: '1px solid #e8edf2', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Status breakdown bar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Invoice distribution</div>
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
              <div style={{ flex: healthData.statusBreakdown.paid, background: '#27ae60' }} />
              <div style={{ flex: healthData.statusBreakdown.pending, background: '#f39c12' }} />
              <div style={{ flex: healthData.statusBreakdown.overdue, background: '#e74c3c' }} />
              <div style={{ flex: healthData.statusBreakdown.failed, background: '#34495e' }} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Financeanalytics;
